// Date / time formatting shared across features. Consolidates helpers that
// were previously duplicated in `components/molecules/StatusTime.tsx`,
// `components/organisms/Activity.tsx`, `features/settings/common.tsx`, and
// `features/email-accounts/detail/format.ts`.

export const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export const WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;

const MS_PER_MIN = 60_000;
const MS_PER_HOUR = MS_PER_MIN * 60;
export const MS_PER_DAY = MS_PER_HOUR * 24;

/** Coerce a Date | ISO string | epoch-ms into a Date. */
export function toDate(input: Date | string | number): Date {
  return input instanceof Date ? input : new Date(input);
}

/** A copy of `d` with the time zeroed to local midnight. */
export function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

/** Whole calendar days between two dates (ignores time-of-day). */
export function diffInCalendarDays(later: Date, earlier: Date): number {
  const ms = startOfDay(later).getTime() - startOfDay(earlier).getTime();
  return Math.round(ms / MS_PER_DAY);
}

/** "Jan 5, 2026". */
export function formatDate(date: Date): string {
  return `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** "January 5, 2026" — full month name, unlike `formatDate`'s abbreviated
 * one. Used where a date is the primary, always-visible label (e.g. the
 * Activity feed's day-section header) rather than a secondary/compact one. */
export function formatFullDate(date: Date): string {
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** "Jan 5, 2026 at 9:07 AM". */
export function fullDateTime(date: Date, timeZone?: string): string {
  const { year, month, day, hours, minutes } = dateParts(date, timeZone);
  return `${MONTHS_SHORT[month]} ${day}, ${year} at ${clock12(hours, minutes)}`;
}

/** "Aug 12" — short absolute date, no year, no time. Used where a row needs
 * a compact date-only stamp (e.g. "Recent Mailbox History") rather than a
 * relative "3h ago" string or the fuller `formatShortDateTime`. */
export function formatShortDate(date: Date, timeZone?: string): string {
  const { month, day } = dateParts(date, timeZone);
  return `${MONTHS_SHORT[month]} ${day}`;
}

/** "Aug 11 · 10:56 AM" — short absolute timestamp, no year (e.g. the Activity feed). */
export function formatShortDateTime(date: Date, timeZone?: string): string {
  const { month, day, hours, minutes } = dateParts(date, timeZone);
  return `${MONTHS_SHORT[month]} ${day} · ${clock12(hours, minutes)}`;
}

/** "10:56 AM" — browser-local clock time, no date. */
export function formatClockTime(date: Date): string {
  return clock12(date.getHours(), date.getMinutes());
}

/** "10:56 AM" from a 0-23 hour. */
function clock12(hours: number, minutes: number): string {
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${minutes.toString().padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
}

/**
 * Calendar/clock parts of `date`: browser-local, or on the wall clock of an IANA `timeZone`
 * (the Activity tab passes the org zone its days are bucketed in). Month is 0-based.
 */
function dateParts(
  date: Date,
  timeZone?: string,
): { year: number; month: number; day: number; hours: number; minutes: number } {
  if (!timeZone) {
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
      hours: date.getHours(),
      minutes: date.getMinutes(),
    };
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return {
    year: part('year'),
    month: part('month') - 1,
    day: part('day'),
    hours: part('hour'),
    minutes: part('minute'),
  };
}

/**
 * Relative-time variants. Each reproduces a previously hand-rolled formatter
 * verbatim — they differ in copy and thresholds, so they are kept as distinct
 * variants rather than forced into one ladder:
 * - `compact` — StatusTime: "just now", "5m ago", "3h ago", "yesterday",
 *   "4d ago", else `formatDate` ("Jan 5, 2026").
 * - `feed`    — Activity feed: as compact but "Yesterday" and an "on Jan 5"
 *   fallback (no year).
 * - `verbose` — settings sessions: "Active now", "5 min ago", "2 hours ago",
 *   "3 days ago" (cutoff 30d), else locale date.
 * - `detail`  — email-account detail: "Just now", "5 min ago", "5 h ago",
 *   "5 d ago" (cutoff 30d), else "5 Jan 2026"; null/invalid → `nullLabel`.
 */
export type RelativeTimeVariant = 'compact' | 'feed' | 'verbose' | 'detail';

export interface RelativeTimeOptions {
  variant?: RelativeTimeVariant;
  /** Reference "now" — Date or epoch-ms. Defaults to the current time. */
  now?: Date | number;
  /** Returned for null / unparseable input. Defaults to "Never". */
  nullLabel?: string;
}

export function formatRelativeTime(
  input: Date | string | number | null | undefined,
  options: RelativeTimeOptions = {},
): string {
  const { variant = 'compact', now, nullLabel = 'Never' } = options;
  if (input == null) return nullLabel;
  const date = toDate(input);
  if (Number.isNaN(date.getTime())) return nullLabel;
  const nowMs = now == null ? Date.now() : now instanceof Date ? now.getTime() : now;

  switch (variant) {
    case 'feed':
      return relativeCompact(date, nowMs, true);
    case 'verbose':
      return relativeVerbose(date, nowMs);
    case 'detail':
      return relativeDetail(date, nowMs);
    case 'compact':
    default:
      return relativeCompact(date, nowMs, false);
  }
}

function relativeCompact(date: Date, nowMs: number, feed: boolean): string {
  const diffMs = nowMs - date.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = diffInCalendarDays(new Date(nowMs), date);
  if (days === 1) return feed ? 'Yesterday' : 'yesterday';
  if (days < 7) return `${days}d ago`;
  return feed
    ? `on ${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}`
    : formatDate(date);
}

function relativeVerbose(date: Date, nowMs: number): string {
  const diff = Math.max(0, nowMs - date.getTime());
  const m = Math.floor(diff / MS_PER_MIN);
  if (m < 1) return 'Active now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

function relativeDetail(date: Date, nowMs: number): string {
  const diff = nowMs - date.getTime();
  if (diff < MS_PER_MIN) return 'Just now';
  if (diff < MS_PER_HOUR) return `${Math.floor(diff / MS_PER_MIN)} min ago`;
  if (diff < MS_PER_DAY) return `${Math.floor(diff / MS_PER_HOUR)} h ago`;
  const days = Math.floor(diff / MS_PER_DAY);
  if (days < 30) return `${days} d ago`;
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * `YYYY-MM-DD` for a Date, read in the viewer's LOCAL calendar (not UTC).
 * Pairs with {@link parseIsoDateLocal} below at the string↔`Date` boundary
 * with calendar-only UI (date filters, `react-day-picker`'s `selected`).
 */
export function toIsoDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * The inverse of {@link toIsoDate}: a `YYYY-MM-DD` string as a LOCAL-midnight
 * `Date`. Deliberately not {@link toDate} — `new Date('2026-05-01')` parses as
 * UTC midnight, which shifts to the previous day west of UTC.
 */
export function parseIsoDateLocal(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year!, (month ?? 1) - 1, day ?? 1);
}

/**
 * Adds `days` (may be negative) to an ISO `YYYY-MM-DD` string, returning
 * another ISO `YYYY-MM-DD` string. Plain UTC calendar-day math — the ISO
 * string itself carries no time zone, so UTC vs local doesn't matter here.
 */
export function addDaysIso(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** The tighter (larger) of two optional ISO lower-bound strings. */
export function tighterMin(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

/** The tighter (smaller) of two optional ISO upper-bound strings. */
export function tighterMax(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}

/**
 * The viewer's IANA time zone, e.g. `"Asia/Kolkata"`.
 *
 * Callers that send this to an API MUST send it explicitly rather than relying
 * on a server-side default — `/reports/warmup/esp/*` computes local day
 * boundaries from it and silently buckets in UTC when it's absent.
 *
 * Falls back to `'UTC'` only when the platform can't resolve a zone at all
 * (`resolvedOptions().timeZone` is `string` in the type but has historically
 * been empty on some engines); never returns `''`, which is undefined
 * behaviour in the server's `toDate(sentAt, tz)`.
 */
export function resolvedTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}
